import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "breelink-theme";

function getSystemTheme() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolve(preference) {
  return preference === "system" ? getSystemTheme() : preference;
}

function applyTheme(theme) {
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  root.classList.toggle("dark", theme === "dark");
  root.style.colorScheme = theme;
}

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return localStorage.getItem(STORAGE_KEY) || "dark";
  });
  const [theme, setTheme] = useState(() => resolve(preference));

  useEffect(() => {
    const next = resolve(preference);
    setTheme(next);
    applyTheme(next);
    localStorage.setItem(STORAGE_KEY, preference);
  }, [preference]);

  // React to OS changes while in "system" mode
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      if ((localStorage.getItem(STORAGE_KEY) || "system") === "system") {
        const next = getSystemTheme();
        setTheme(next);
        applyTheme(next);
      }
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const setPreference = useCallback((p) => setPreferenceState(p), []);
  const toggle = useCallback(() => {
    setPreferenceState((prev) => (resolve(prev) === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeContext.Provider value={{ preference, setPreference, theme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
};
